import { useEffect, useState } from "react";
import { Plus, Trash, ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "../components/Shell";
import ExportButton from "../components/ExportButton";
import { api, currency, todayKey } from "../lib/api";
import { Modal, Field } from "./Fuel";
import { useStation } from "../lib/station";

export default function Cash() {
  const { activeId } = useStation();
  const [shifts, setShifts] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const refresh = async () => {
    if (!activeId) return;
    setShifts(await api.listShifts(activeId));
  };
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!activeId) return null;

  const today = todayKey();
  const todayShifts = shifts.filter((s) => s.date === today);
  const totalDiscrepancy = todayShifts.reduce((a, s) => a + (s.discrepancy || 0), 0);
  const totalCash = todayShifts.reduce((a, s) => a + (s.cash_sales || 0), 0);
  const totalCard = todayShifts.reduce((a, s) => a + (s.card_sales || 0), 0);

  const handleDelete = async (id) => {
    await api.deleteShift(id);
    toast.success("Shift removed");
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="Cash"
        subtitle="Shift reconciliation"
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="shifts" testId="export-shifts" />
            <button
              onClick={() => setShowForm(true)}
              data-testid="add-shift-btn"
              className="h-11 px-4 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] font-bold text-sm uppercase tracking-wider flex items-center gap-2"
            >
              <Plus size={16} weight="bold" /> Shift
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="cash-today-stats">
        <Stat label="Cash" value={currency(totalCash)} />
        <Stat label="Card" value={currency(totalCard)} />
        <Stat
          label="Variance"
          value={currency(totalDiscrepancy)}
          accent={Math.abs(totalDiscrepancy) < 0.01 ? "#10B981" : totalDiscrepancy < 0 ? "#EF4444" : "#F59E0B"}
        />
      </div>

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mb-3">Recent Shifts</h2>
      <div className="grid gap-2" data-testid="shift-list">
        {shifts.length === 0 && (
          <div className="surface rounded-lg p-6 text-center text-sm text-gray-500">No shifts logged yet.</div>
        )}
        {shifts.map((s) => {
          const off = Math.abs(s.discrepancy) >= 0.01;
          const negative = s.discrepancy < 0;
          return (
            <div
              key={s.id}
              className={`surface rounded-lg p-4 ${off ? (negative ? "border-l-4 border-l-[#EF4444]" : "border-l-4 border-l-[#F59E0B]") : ""}`}
              data-testid={`shift-row-${s.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-bold text-white">
                    {s.date} · <span className="capitalize">{s.shift_name}</span>
                  </div>
                  <div className="text-xs text-gray-500">{s.employee}</div>
                </div>
                <div className="text-right">
                  <div className={`flex items-center gap-1 font-bold ${off ? (negative ? "text-[#EF4444]" : "text-[#F59E0B]") : "text-[#10B981]"}`}>
                    {off ? negative ? <ArrowDown size={14} weight="bold" /> : <ArrowUp size={14} weight="bold" /> : null}
                    {currency(s.discrepancy)}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">Variance</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
                <Mini label="Open" v={currency(s.opening_cash)} />
                <Mini label="Cash" v={currency(s.cash_sales)} />
                <Mini label="Card" v={currency(s.card_sales)} />
                <Mini label="Close" v={currency(s.closing_cash)} />
              </div>
              <button
                onClick={() => handleDelete(s.id)}
                data-testid={`delete-shift-${s.id}`}
                className="mt-3 text-xs text-gray-500 hover:text-[#EF4444] flex items-center gap-1"
              >
                <Trash size={12} /> Delete
              </button>
            </div>
          );
        })}
      </div>

      {showForm && <ShiftForm stationId={activeId} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); refresh(); }} />}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="surface rounded-lg p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">{label}</div>
      <div className="font-display font-black text-xl mt-1" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}

function Mini({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="font-mono text-gray-200">{v}</div>
    </div>
  );
}

function ShiftForm({ stationId, onClose, onCreated }) {
  const [form, setForm] = useState({
    date: todayKey(),
    shift_name: "morning",
    employee: "",
    opening_cash: "",
    closing_cash: "",
    cash_sales: "",
    card_sales: "",
    gallons_regular: "",
    gallons_mid: "",
    gallons_premium: "",
    price_regular: "",
    price_mid: "",
    price_premium: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const opening = parseFloat(form.opening_cash) || 0;
  const closing = parseFloat(form.closing_cash) || 0;
  const cashSales = parseFloat(form.cash_sales) || 0;
  const expected = opening + cashSales;
  const variance = closing - expected;
  const off = Math.abs(variance) >= 0.01;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee) {
      toast.error("Employee name required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { station_id: stationId, ...form };
      [
        "opening_cash",
        "closing_cash",
        "cash_sales",
        "card_sales",
        "gallons_regular",
        "gallons_mid",
        "gallons_premium",
        "price_regular",
        "price_mid",
        "price_premium",
      ].forEach((k) => {
        payload[k] = parseFloat(payload[k]) || 0;
      });
      await api.createShift(payload);
      toast.success("Shift saved");
      onCreated();
    } catch {
      toast.error("Failed to save shift");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Log Shift" testId="shift-modal">
      <form onSubmit={submit} className="grid gap-3" data-testid="shift-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date">
            <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="input-dark" data-testid="shift-date" />
          </Field>
          <Field label="Shift">
            <select className="input-dark" value={form.shift_name} onChange={(e) => set("shift_name", e.target.value)} data-testid="shift-name">
              <option value="morning">Morning</option>
              <option value="evening">Evening</option>
              <option value="night">Night</option>
            </select>
          </Field>
        </div>
        <Field label="Employee">
          <input type="text" value={form.employee} onChange={(e) => set("employee", e.target.value)} placeholder="Name" className="input-dark" data-testid="shift-employee" required />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Opening Cash">
            <input type="number" step="0.01" value={form.opening_cash} onChange={(e) => set("opening_cash", e.target.value)} className="input-dark" data-testid="shift-opening" />
          </Field>
          <Field label="Closing Cash">
            <input type="number" step="0.01" value={form.closing_cash} onChange={(e) => set("closing_cash", e.target.value)} className="input-dark" data-testid="shift-closing" />
          </Field>
          <Field label="Cash Sales">
            <input type="number" step="0.01" value={form.cash_sales} onChange={(e) => set("cash_sales", e.target.value)} className="input-dark" data-testid="shift-cashsales" />
          </Field>
          <Field label="Card Sales">
            <input type="number" step="0.01" value={form.card_sales} onChange={(e) => set("card_sales", e.target.value)} className="input-dark" data-testid="shift-cardsales" />
          </Field>
        </div>

        <div
          className={`rounded-lg p-3 text-sm border ${off ? (variance < 0 ? "border-[#EF4444]/40 bg-[#EF4444]/10" : "border-[#F59E0B]/40 bg-[#F59E0B]/10") : "border-[#10B981]/40 bg-[#10B981]/10"}`}
          data-testid="shift-variance-preview"
        >
          <div className="flex justify-between">
            <span className="text-gray-300">Expected close</span>
            <span className="font-mono">{currency(expected)}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-gray-300 font-bold">Variance</span>
            <span className={`font-mono font-bold ${off ? (variance < 0 ? "text-[#EF4444]" : "text-[#F59E0B]") : "text-[#10B981]"}`}>
              {currency(variance)}
            </span>
          </div>
        </div>

        <details className="surface rounded-lg p-3">
          <summary className="text-xs font-bold uppercase tracking-wider text-gray-400 cursor-pointer">
            Gallons sold (optional)
          </summary>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {["regular", "mid", "premium"].map((f) => (
              <Field key={f} label={f === "mid" ? "Mid" : f}>
                <input type="number" step="0.01" value={form[`gallons_${f}`]} onChange={(e) => set(`gallons_${f}`, e.target.value)} className="input-dark" data-testid={`shift-gallons-${f}`} />
              </Field>
            ))}
            {["regular", "mid", "premium"].map((f) => (
              <Field key={`p-${f}`} label={`${f === "mid" ? "Mid" : f} $/gal`}>
                <input type="number" step="0.001" value={form[`price_${f}`]} onChange={(e) => set(`price_${f}`, e.target.value)} className="input-dark" data-testid={`shift-price-${f}`} />
              </Field>
            ))}
          </div>
        </details>

        <button type="submit" disabled={submitting} data-testid="shift-submit" className="h-14 mt-1 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 font-bold uppercase tracking-wider text-white">
          {submitting ? "Saving..." : "Save Shift"}
        </button>
      </form>
    </Modal>
  );
}
